import { DialogComponent } from '@theme/dialog';
import { StandardEvents } from '@shopify/events';

const SESSION_STORAGE_KEY_PREFIX = 'bp:cap-upsell-shown:';

// Upsell tracking: custom events go to Microsoft Clarity (if present). Never throws.
const trackUpsell = (name) => {
  try {
    if (typeof window.clarity === 'function') window.clarity('event', name);
  } catch (e) {
    /* tracking must never break the popup */
  }
};

// Hidden line-item property (underscore = not shown to customers) so orders can be
// attributed to the popup. The kind comes from the card's data-upsell-kind (set in
// sections/matching-cap-popup.liquid): 'special-box', 'accessory' or 'cap'.
const tagPopupForms = (root) => {
  root.querySelectorAll('.cap-upsell__card').forEach((card) => {
    const form = card.querySelector('form');
    if (!form || form.querySelector('input[name="properties[_upsell]"]')) return;
    const isBox = Boolean(card.querySelector('a[href*="block-p-speical-box"]'));
    const kind = card.dataset.upsellKind || (isBox ? 'special-box' : 'cap');
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'properties[_upsell]';
    input.value = kind;
    form.appendChild(input);
  });
};

// Mirrors the "Clothing" smart collection's TYPE rules (Hoodies/T-Shirt/Jacket/Pants/Shorts).
// Kept in sync manually since a cart line item only exposes product_type, not collection
// membership, so we can't just check `collections['clothing']` from here.
const APPAREL_PRODUCT_TYPES = new Set(['T-Shirt', 'Hoodies', 'Jacket', 'Pants', 'Shorts']);

function categoryFor(productType) {
  if (productType === 'Shoes') return 'shoes';
  if (productType === 'Slipper') return 'slippers';
  if (APPAREL_PRODUCT_TYPES.has(productType)) return 'apparel';
  return null;
}

class CapUpsellDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #handleCartUpdate = (event) => {
    if (event.action !== 'add' || !event.promise) return;

    const wasAlreadyOpen = Boolean(this.refs.dialog?.open);

    event.promise
      .then(({ detail }) => {
        if (detail?.didError) return;

        if (wasAlreadyOpen) {
          // An add while the popup is open = the shopper used the popup's own add button.
          trackUpsell('upsell_added_cap');
          this.closeDialog();
          return;
        }

        const items = detail?.items ?? [];
        const addedItem = items.find((item) => String(item.product_id) === String(detail?.productId));
        if (!addedItem || !addedItem.handle) return;

        // Each tracked category (shoes / apparel / slippers) gets its own one-shot session flag,
        // so e.g. the first shoe add and the first apparel add can each trigger their own upsell
        // once, independently, in the same session.
        const category = categoryFor(addedItem.product_type);
        if (!category) return;

        const storageKey = `${SESSION_STORAGE_KEY_PREFIX}${category}`;
        if (sessionStorage.getItem(storageKey)) return;

        this.#showFor(addedItem.handle, storageKey);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cap-upsell] Event promise rejected:', error);
      });
  };

  #showFor(handle, storageKey) {
    fetch(`/products/${handle}?section_id=matching-cap-popup`)
      .then((response) => response.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const content = doc.querySelector('[data-cap-upsell-content]');
        const modalContent = document.getElementById('cap-upsell-modal-content');
        if (!content || !modalContent || content.dataset.hasCap !== 'true') return;

        sessionStorage.setItem(storageKey, '1');

        modalContent.innerHTML = content.innerHTML;
        tagPopupForms(modalContent);
        this.showDialog();
        trackUpsell('upsell_shown_cap');
      })
      .catch(() => {});
  }
}

if (!customElements.get('cap-upsell-dialog')) {
  customElements.define('cap-upsell-dialog', CapUpsellDialog);
}